import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { toast } from "sonner";
import { presentMutationError, toastError } from "../../../utils/showApiErrorToast";
import { useHistory, useLocation } from "react-router-dom";
import { useActiveBrokerAccount } from "../../../hooks/useActiveBrokerAccount";
import { formatAccountDisplayLabelShort } from "../../../services/liquidBrokersApi";
import { useUserNotificationTimezone } from "../../../hooks/useUserNotificationTimezone";

import {
  fetchAvailableInstruments,
  fetchCandleTimeframePreview,
  generateReport,
  getReportTypes,
  getReportStrategyDetail,
  listReportStrategies,
  listReports,
  ReportsApiError,
  updateReportStrategy,
} from "../../../services/reportsApi";
import type { ReportCandleTimeframe } from "../../../types/reports";
import { useInstrumentResolve } from "../../../hooks/useInstrumentResolve";
import { buildConnectBrokerManagementOpenUrl } from "../Profile/connectBroker/connectBrokerWizardConstants";
import {
  buildMarketDataConnectOptionHref,
  marketDataConnectGateFromApiError,
  marketDataConnectGateFromConnectorRequired,
  marketDataConnectGateFromInstrumentRow,
  marketDataOhlcBlockedNoticeFromInstrumentRow,
  marketDataConnectGateFromResolveResult,
  isMarketDataAvailabilityVerificationPending,
  MARKET_DATA_PREFERENCE_SETTINGS_PATH,
  needsMarketDataAvailabilityResolveForRow,
  type MarketDataConnectGateState,
} from "../../../utils/marketDataConnectGate";
import { instrumentChartsBlocked } from "../../../utils/instrumentMarketDataReadiness";
import { useInstrumentSetup } from "../../../contexts/InstrumentSetupContext";
import { useWizardMarketDataOhlcGate } from "../../../hooks/useWizardMarketDataOhlcGate";
import { handleMarketDataUnresolvedError } from "../../../utils/handleMarketDataUnresolved";
import { MarketDataUnresolvedError } from "../../../utils/marketDataResolution";
import { MarketDataUnavailableError, marketDataUnavailableFromCandleTimeframePreview, candleTimeframePreviewFetchErrorState } from "../../../utils/marketDataUnavailable";
import type { MarketDataUnavailablePayload } from "../../../types/marketDataUnavailable";
import { useRateLimitCooldown } from "../../../hooks/useRateLimitCooldown";
import {
  buildPerformanceReviewSymbolsPayload,
  effectiveIncludeMarketContextForGenerate,
  EXIT_PLACEMENT_SUBTITLE,
  isSymbolRequiredForGenerate,
  isTradeManagementReportType,
  mapReportTypeOptionsForDisplay,
  normalizeReportTypeKey,
  reportSymbolScopeMode,
  showMarketContextFieldsOnGenerate,
  showPerformanceReviewSymbolFieldsOnGenerate,
} from "../../../utils/reportTypeRules";
import { DateRange } from "../../../types/trade";
import type { Report, ReportStrategyResponse, ReportTypeOption } from "../../../types/reports";
import type { WorkspaceCreateContext } from "../../../types/workspace";
import type {
  AvailableInstrumentRow,
  CandleTimeframePreviewResponse,
} from "../../../types/tradeManagementReport";
import { buildCandleTimeframePreviewUi } from "../../../utils/candleTimeframePreview";
import {
  needsMarketDataSourceChoiceForRow,
} from "../../../utils/marketDataSourceChoice";
import {
  computeGenerateMdBlocked,
  isInstrumentMarketDataReadyForCharts,
  shouldGuideInstrumentMarketDataSetup,
} from "../../../utils/instrumentMarketDataTrust";
import {
  userFacingInstrumentsSelectionHint,
} from "../../../utils/reportInstrumentSelection";
import type { MarketDataSourceOption } from "../../../types/marketDataSources";
import { buildAnalyticsWindowSnakeFields } from "../../../utils/analyticsWindowParams";
import { getReportDisplayTitle } from "../../../utils/reportWindowLabel";
import {
  equalDateRange,
  getInitialDashboardDateWindow,
  persistStateFromTradeLog,
  preferredPresetFromPersisted,
  presetToLocalDateRange,
  reportsStateFromPersisted,
  writeDashboardDateWindow,
  type DashboardDatePresetTimeframe,
  type DashboardDateRangeChangeOptions,
} from "../../../utils/dashboardDateWindowStorage";
import { useDashboardDateWindowListener } from "../../../hooks/useDashboardDateWindowListener";
import type { GenerateReportStrategyMode, GenerateReportTradeSideFilter } from "./GenerateReportWizard";
import { getGenerateReportBlockedReason, isGenerateReportReady } from "../../../utils/generateReportBlockedReason";
import {
  formatWeeklyReportQuotaMessage,
  isWeeklyReportQuotaReached,
} from "../../../types/billing";
import { useBillingContext } from "../../../contexts/BillingContext";
import {
  REPORT_CANDLE_TIMEFRAME_STORAGE_KEY,
  DEFAULT_REPORT_CANDLE_TIMEFRAME,
  REPORT_TITLE_MAX,
  STRATEGY_DESCRIPTION_SOFT_LIMIT,
  STRATEGY_NAME_SOFT_LIMIT,
} from "./generateReportConstants";

function isTradeScopeDateRangeValid(dateRange: DateRange): boolean {
  if (dateRange.startDate && dateRange.endDate) return true;
  return !dateRange.startDate && !dateRange.endDate;
}

function reportsPageDateStateFromPersisted(p: ReturnType<typeof getInitialDashboardDateWindow>): {
  timeframe: string;
  dateRange: DateRange;
} {
  const rs = reportsStateFromPersisted(p);
  if (rs.dateRange.startDate && rs.dateRange.endDate) {
    return { timeframe: rs.timeframe, dateRange: rs.dateRange };
  }
  const r = presetToLocalDateRange(rs.timeframe);
  if (r) return { timeframe: rs.timeframe, dateRange: r };
  return { timeframe: rs.timeframe, dateRange: { startDate: null, endDate: null } };
}

export type UseGenerateReportFlowOptions = {
  hasInflightReport?: boolean;
  /** When set, skip /reports navigation and pass workspace fields on generate. */
  embedMode?: boolean;
  prefillStrategyId?: string | null;
  /** Display name for a canvas-prefilled strategy (review step / omit strategy step). */
  prefillStrategyName?: string | null;
  lockBrokerAccountCode?: string;
  workspaceContext?: WorkspaceCreateContext & { position?: { x: number; y: number } };
  onReportGenerated?: (result: { report: Report; accepted: boolean }) => void;
  /** Fired as soon as generate starts in embed mode (close dialog UI without unmounting the flow).
   * May return a live parent/position so the API matches the optimistic canvas node. */
  onEmbedGenerateStart?: (preview: {
    title: string;
    strategyEntityId?: string | null;
  }) => void | { parentNodeId: string; position: { x: number; y: number } };
  /** Fired when embed generate fails after the dialog was already hidden. */
  onEmbedGenerateFailed?: (options?: { keepDialogOpen?: boolean }) => void;
  onEmbedCancel?: () => void;
};

export function useGenerateReportFlow(options?: UseGenerateReportFlowOptions) {
  const history = useHistory();
  const location = useLocation();
  const { status: billingStatus } = useBillingContext();
  const weeklyReportQuotaReached = isWeeklyReportQuotaReached(billingStatus);
  const weeklyReportQuotaMessage = weeklyReportQuotaReached
    ? formatWeeklyReportQuotaMessage(billingStatus)
    : null;
  const {
    activeAccountCode,
    activeBrokerName,
    accounts,
    loading: brokerAccountsLoading,
    setActiveTradeAccount,
  } = useActiveBrokerAccount();
  const lockBrokerAccountCode = options?.lockBrokerAccountCode?.trim() || "";
  const prefillStrategyId = options?.prefillStrategyId?.trim() || "";
  const prefillStrategyName = options?.prefillStrategyName?.trim() || "";
  const effectiveBrokerAccountCode =
    lockBrokerAccountCode || activeAccountCode || "";

  const [reportsInitialWindow] = useState(() =>
    reportsPageDateStateFromPersisted(getInitialDashboardDateWindow())
  );
  const [timeframe, setTimeframe] = useState<string>(reportsInitialWindow.timeframe);
  const [dateRange, setDateRange] = useState<DateRange>(reportsInitialWindow.dateRange);
  const [selectedPresetId, setSelectedPresetId] =
    useState<DashboardDatePresetTimeframe | null>(() =>
      preferredPresetFromPersisted(getInitialDashboardDateWindow())
    );
  const [symbol, setSymbol] = useState("");
  const [performanceReviewAllSymbols, setPerformanceReviewAllSymbols] = useState(true);
  const [performanceReviewSymbols, setPerformanceReviewSymbols] = useState<string[]>([]);
  const [resolvedAssetClass, setResolvedAssetClass] = useState<string | undefined>(undefined);
  const [tradeSideFilter, setTradeSideFilter] = useState<GenerateReportTradeSideFilter>("both");
  const [availableInstruments, setAvailableInstruments] = useState<AvailableInstrumentRow[]>([]);
  const [instrumentsSelectionHint, setInstrumentsSelectionHint] = useState<string | null>(null);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);
  const instrumentsInflightRef = useRef<{ key: string; promise: Promise<void> } | null>(null);
  const [fetchedInflightReport, setFetchedInflightReport] = useState(false);
  const [hasInflightFromList, setHasInflightFromList] = useState(false);

  const instrumentResolve = useInstrumentResolve(effectiveBrokerAccountCode);
  const { openSetup, isOpen: instrumentSetupOpen } = useInstrumentSetup();
  const instrumentSetupWasOpenRef = useRef(false);
  const [marketDataAvailabilityVerifiedForSymbol, setMarketDataAvailabilityVerifiedForSymbol] =
    useState<string | null>(null);

  const [reportTypeOptions, setReportTypeOptions] = useState<ReportTypeOption[]>([]);
  const [selectedReportType, setSelectedReportType] = useState("");
  const [reportTypesLoading, setReportTypesLoading] = useState(false);
  const [marketContextSpec] = useState<"pine_v1" | "v1">("pine_v1");
  const [candleTimeframe, setCandleTimeframe] = useState<"" | "1m" | "5m" | "15m" | "30m" | "1h" | "4h">(() => {
    try {
      const raw = window.localStorage.getItem(REPORT_CANDLE_TIMEFRAME_STORAGE_KEY);
      if (
        raw === "1m" ||
        raw === "5m" ||
        raw === "15m" ||
        raw === "30m" ||
        raw === "1h" ||
        raw === "4h"
      ) {
        return raw;
      }
    } catch {
      // no-op
    }
    return DEFAULT_REPORT_CANDLE_TIMEFRAME;
  });

  const [strategyMode, setStrategyMode] = useState<GenerateReportStrategyMode>("existing");
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const [strategyOptions, setStrategyOptions] = useState<ReportStrategyResponse[]>([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState("");
  const [strategyName, setStrategyName] = useState("");
  const [strategyDescription, setStrategyDescription] = useState("");
  /** Resolved via GET strategy when canvas meta title is missing / generic. */
  const [fetchedPrefillStrategyName, setFetchedPrefillStrategyName] = useState("");

  const effectivePrefillStrategyName =
    fetchedPrefillStrategyName.trim() || prefillStrategyName || "";

  /** Keep canvas-prefilled strategy visible even when list/detail has not returned it yet. */
  const strategyOptionsForWizard = useMemo(() => {
    if (!prefillStrategyId) return strategyOptions;
    const fromList = strategyOptions.find((s) => s.id === prefillStrategyId);
    if (fromList) {
      const listName = fromList.name?.trim() || "";
      // List sometimes returns a row without a usable name; overlay the canvas/GET title.
      if (
        effectivePrefillStrategyName &&
        (!listName || listName.toLowerCase() === "strategy")
      ) {
        return strategyOptions.map((s) =>
          s.id === prefillStrategyId ? { ...s, name: effectivePrefillStrategyName } : s
        );
      }
      return strategyOptions;
    }
    const stub: ReportStrategyResponse = {
      id: prefillStrategyId,
      user_id: "",
      name: effectivePrefillStrategyName,
      description: "",
      is_archived: false,
      created_at: "",
      updated_at: "",
    };
    return [stub, ...strategyOptions];
  }, [strategyOptions, prefillStrategyId, effectivePrefillStrategyName]);

  const { isOpen: editStrategyOpen, onOpen: onEditStrategyOpen, onClose: onEditStrategyClose } = useDisclosure();
  const [editStrategyId, setEditStrategyId] = useState("");
  const [editStrategyName, setEditStrategyName] = useState("");
  const [editStrategyDescription, setEditStrategyDescription] = useState("");
  const [editStrategyArchived, setEditStrategyArchived] = useState(false);
  const [savingStrategy, setSavingStrategy] = useState(false);

  const [reportTitle, setReportTitle] = useState("");

  const [generating, setGenerating] = useState(false);
  const [marketDataConnectGate, setMarketDataConnectGate] =
    useState<MarketDataConnectGateState | null>(null);
  const [marketDataUnavailableGate, setMarketDataUnavailableGate] =
    useState<MarketDataUnavailablePayload | null>(null);
  const [wizardResumeStepKey, setWizardResumeStepKey] = useState<string | null>(null);
  const [wizardActiveStepIndex, setWizardActiveStepIndex] = useState(0);

  useEffect(() => {
    if (prefillStrategyId) {
      setStrategyMode("existing");
      setSelectedStrategyId(prefillStrategyId);
      return;
    }
    // Account-level workspace create: default to no strategy (report hangs off account).
    if (options?.embedMode) {
      setStrategyMode("none");
      setSelectedStrategyId("");
    }
  }, [prefillStrategyId, options?.embedMode]);

  useEffect(() => {
    if (!prefillStrategyId) {
      setFetchedPrefillStrategyName("");
      return;
    }
    // Canvas already gave a real title — skip the GET.
    const canvasName = prefillStrategyName.trim();
    if (canvasName && canvasName.toLowerCase() !== "strategy") {
      setFetchedPrefillStrategyName("");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const detail = await getReportStrategyDetail(prefillStrategyId);
        const name = detail.name?.trim() || "";
        if (!cancelled && name && name.toLowerCase() !== "strategy") {
          setFetchedPrefillStrategyName(name);
        }
      } catch {
        // Review can still fall back to list options / stub.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prefillStrategyId, prefillStrategyName]);

  const reportGenerateRateLimit = useRateLimitCooldown("report-generate");
  const [candleTimeframePreview, setCandleTimeframePreview] =
    useState<CandleTimeframePreviewResponse | null>(null);
  const [candleTimeframePreviewLoading, setCandleTimeframePreviewLoading] = useState(false);
  const [candleTimeframePreviewFailed, setCandleTimeframePreviewFailed] = useState(false);

  const { timezone: notificationTimezone, revision: notificationTzRevision } = useUserNotificationTimezone();
  const reportingTimeZone =
    notificationTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";

  const handleDateRangeChange = useCallback(
    (next: DateRange, options?: DashboardDateRangeChangeOptions) => {
      setDateRange(next);
      if (options?.presetId) {
        setSelectedPresetId(options.presetId);
        setTimeframe(options.presetId);
      } else {
        setSelectedPresetId(null);
        if (!next.startDate && !next.endDate) {
          setTimeframe("all-time");
        }
      }
    },
    []
  );

  /** Prefill date range (and optionally account) from notification / deep-link query params. */
  useEffect(() => {
    const params = new URLSearchParams(
      location.search.startsWith("?") ? location.search.slice(1) : location.search
    );
    const startRaw = params.get("local_start_date")?.trim() || "";
    const endRaw = params.get("local_end_date")?.trim() || "";
    const startMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(startRaw);
    const endMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(endRaw);
    if (startMatch && endMatch) {
      const startDate = new Date(
        Number(startMatch[1]),
        Number(startMatch[2]) - 1,
        Number(startMatch[3]),
        0,
        0,
        0,
        0
      );
      const endDate = new Date(
        Number(endMatch[1]),
        Number(endMatch[2]) - 1,
        Number(endMatch[3]),
        0,
        0,
        0,
        0
      );
      if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
        setDateRange({ startDate, endDate });
        setSelectedPresetId(null);
      }
    }

    const brokerCode = params.get("broker_account_code")?.trim() || "";
    if (brokerCode && accounts.length > 0 && !options?.lockBrokerAccountCode) {
      const acc = accounts.find((a) => a.accountCode === brokerCode);
      if (acc && (acc.accountCode !== activeAccountCode || acc.brokerName !== activeBrokerName)) {
        void setActiveTradeAccount(acc.brokerName, acc.accountCode);
      }
    }
  }, [
    accounts,
    activeAccountCode,
    activeBrokerName,
    location.search,
    options?.lockBrokerAccountCode,
    setActiveTradeAccount,
  ]);

  useEffect(() => {
    writeDashboardDateWindow(
      persistStateFromTradeLog(dateRange, timeframe, selectedPresetId)
    );
  }, [dateRange, timeframe, selectedPresetId]);

  const includeMarketContextForGenerate = useMemo(
    () => effectiveIncludeMarketContextForGenerate(selectedReportType),
    [selectedReportType]
  );

  const showMarketContextFields = useMemo(
    () => showMarketContextFieldsOnGenerate(selectedReportType),
    [selectedReportType]
  );

  const wizardMdGate = useWizardMarketDataOhlcGate({
    brokerAccountCode: effectiveBrokerAccountCode,
    symbol: symbol.trim() || undefined,
    enabled: showMarketContextFields && Boolean(effectiveBrokerAccountCode.trim()),
  });

  const showPerformanceReviewSymbolFields = useMemo(
    () => showPerformanceReviewSymbolFieldsOnGenerate(selectedReportType),
    [selectedReportType]
  );

  const showSymbolScopeFields = showMarketContextFields || showPerformanceReviewSymbolFields;

  const symbolRequired = useMemo(
    () =>
      selectedReportType
        ? isSymbolRequiredForGenerate(selectedReportType, includeMarketContextForGenerate)
        : false,
    [selectedReportType, includeMarketContextForGenerate]
  );

  const onReportTypeChange = useCallback(
    (nextReportType: string) => {
      const prevMode = reportSymbolScopeMode(selectedReportType);
      const nextMode = reportSymbolScopeMode(nextReportType);

      if (prevMode !== nextMode) {
        if (prevMode === "performance_review" && nextMode === "market_context") {
          if (!performanceReviewAllSymbols && performanceReviewSymbols.length > 0) {
            setSymbol(performanceReviewSymbols[0]!);
          }
          setPerformanceReviewAllSymbols(true);
          setPerformanceReviewSymbols([]);
        } else if (prevMode === "market_context" && nextMode === "performance_review") {
          const trimmed = symbol.trim();
          if (trimmed) {
            setPerformanceReviewAllSymbols(false);
            setPerformanceReviewSymbols([trimmed]);
          } else {
            setPerformanceReviewAllSymbols(true);
            setPerformanceReviewSymbols([]);
          }
          setSymbol("");
          setResolvedAssetClass(undefined);
          instrumentResolve.reset();
          setMarketDataConnectGate(null);
          setMarketDataUnavailableGate(null);
        } else {
          if (nextMode !== "market_context") {
            setSymbol("");
            setResolvedAssetClass(undefined);
            instrumentResolve.reset();
            setMarketDataConnectGate(null);
            setMarketDataUnavailableGate(null);
          }
          if (nextMode !== "performance_review") {
            setPerformanceReviewAllSymbols(true);
            setPerformanceReviewSymbols([]);
          }
        }
      }

      setSelectedReportType(nextReportType);
    },
    [
      instrumentResolve,
      performanceReviewAllSymbols,
      performanceReviewSymbols,
      selectedReportType,
      symbol,
    ]
  );

  const brokerAccountOptions = useMemo(() => {
    // Radix Select portals every matching SelectItem's label into SelectValue.
    // Duplicate accountCode values therefore render twice in the trigger.
    const seen = new Set<string>();
    const mapped: { value: string; label: string }[] = [];
    for (const acc of accounts) {
      const code = acc.accountCode?.trim();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      mapped.push({
        value: code,
        label: formatAccountDisplayLabelShort(acc),
      });
    }
    if (!lockBrokerAccountCode) return mapped;
    const locked = mapped.find((opt) => opt.value === lockBrokerAccountCode);
    return locked
      ? [locked]
      : [{ value: lockBrokerAccountCode, label: lockBrokerAccountCode }];
  }, [accounts, lockBrokerAccountCode]);

  const onBrokerAccountChange = useCallback(
    (accountCode: string) => {
      const acc = accounts.find((a) => a.accountCode === accountCode);
      if (!acc) return;
      void setActiveTradeAccount(acc.brokerName, acc.accountCode);
    },
    [accounts, setActiveTradeAccount]
  );

  const analyticsBundleWindow = useMemo(
    () => buildAnalyticsWindowSnakeFields(dateRange, timeframe, reportingTimeZone),
    [dateRange, timeframe, reportingTimeZone, notificationTzRevision]
  );

  const windowSummaryLabel = useMemo(() => {
    if (dateRange.startDate && dateRange.endDate) {
      const fmt = (d: Date) =>
        d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      return `${fmt(dateRange.startDate)} → ${fmt(dateRange.endDate)}`;
    }
    if (!dateRange.startDate && !dateRange.endDate) {
      const presetLabels: Record<string, string> = {
        today: "Today",
        "7-days": "Last 7 days",
        "14-days": "Last 14 days",
        "30-days": "Last 30 days",
        "all-time": "All time (~365 days)",
      };
      return presetLabels[timeframe] ?? (timeframe || "All time");
    }
    return "Select dates";
  }, [dateRange.endDate, dateRange.startDate, timeframe]);

  const loadAvailableInstruments = useCallback(async () => {
    if (!effectiveBrokerAccountCode) {
      setAvailableInstruments([]);
      instrumentsInflightRef.current = null;
      return;
    }
    const key = JSON.stringify({
      broker_account_code: effectiveBrokerAccountCode,
      trade_side_filter: tradeSideFilter,
      ...analyticsBundleWindow,
    });
    if (instrumentsInflightRef.current?.key === key) {
      return instrumentsInflightRef.current.promise;
    }
    const run = async () => {
      setInstrumentsLoading(true);
      try {
        const data = await fetchAvailableInstruments({
          broker_account_code: effectiveBrokerAccountCode,
          trade_side_filter: tradeSideFilter,
          ...analyticsBundleWindow,
        });
        const instruments = Array.isArray(data.instruments) ? data.instruments : [];
        setAvailableInstruments(instruments);
        const hint =
          typeof data.selection_hint === "string" ? data.selection_hint.trim() : "";
        setInstrumentsSelectionHint(userFacingInstrumentsSelectionHint(hint));
      } catch {
        setAvailableInstruments([]);
        setInstrumentsSelectionHint(null);
      } finally {
        setInstrumentsLoading(false);
        if (instrumentsInflightRef.current?.key === key) {
          instrumentsInflightRef.current = null;
        }
      }
    };
    const promise = run();
    instrumentsInflightRef.current = { key, promise };
    return promise;
  }, [effectiveBrokerAccountCode, tradeSideFilter, analyticsBundleWindow]);

  useEffect(() => {
    if (options?.hasInflightReport !== undefined) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await listReports({ status: "processing", limit: 1, offset: 0 });
        if (cancelled) return;
        const inflight = (data.reports ?? []).some((r) => r.status === "processing");
        setHasInflightFromList(inflight);
      } catch {
        if (!cancelled) setHasInflightFromList(false);
      } finally {
        if (!cancelled) setFetchedInflightReport(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [options?.hasInflightReport]);

  const hasInflightReport =
    options?.hasInflightReport ?? hasInflightFromList;

  useDashboardDateWindowListener((p) => {
    setSelectedPresetId(preferredPresetFromPersisted(p));
    const rs = reportsStateFromPersisted(p);
    if (rs.dateRange.startDate && rs.dateRange.endDate) {
      setTimeframe((prev) => (prev === rs.timeframe ? prev : rs.timeframe));
      setDateRange((prev) => (equalDateRange(prev, rs.dateRange) ? prev : rs.dateRange));
      return;
    }
    const r = presetToLocalDateRange(rs.timeframe);
    if (r) {
      setTimeframe((prev) => (prev === rs.timeframe ? prev : rs.timeframe));
      setDateRange((prev) => (equalDateRange(prev, r) ? prev : r));
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(REPORT_CANDLE_TIMEFRAME_STORAGE_KEY, candleTimeframe);
    } catch {
      // no-op
    }
  }, [candleTimeframe]);

  useEffect(() => {
    if (!showMarketContextFields || !symbol || instrumentsLoading) return;
    if (availableInstruments.length === 0) return;
      const row = availableInstruments.find((r) => r.symbol === symbol);
    if (!row) {
      setSymbol("");
    }
  }, [showMarketContextFields, symbol, availableInstruments, instrumentsLoading]);

  useEffect(() => {
    if (performanceReviewAllSymbols || availableInstruments.length === 0) return;
    const allowed = new Set(availableInstruments.map((row) => row.symbol));
    setPerformanceReviewSymbols((prev) => {
      const next = prev.filter((value) => allowed.has(value));
      return next.length === prev.length ? prev : next;
    });
  }, [availableInstruments, performanceReviewAllSymbols]);

  useEffect(() => {
    let cancelled = false;
    const loadReportTypes = async () => {
      setReportTypesLoading(true);
      try {
        const data = await getReportTypes();
        if (cancelled) return;
        const options = mapReportTypeOptionsForDisplay(
          Array.isArray(data.report_types) ? data.report_types : []
        );
        setReportTypeOptions(options);
        const hasDefault = options.some((o) => o.key === data.default_report_type);
        const fallback = options[0]?.key || "";
        const nextDefault = hasDefault ? data.default_report_type : fallback;
        const params = new URLSearchParams(
          location.search.startsWith("?") ? location.search.slice(1) : location.search
        );
        const qpReportType = params.get("report_type")?.trim();
        const qpNormalized = qpReportType ? normalizeReportTypeKey(qpReportType) : "";
        setSelectedReportType((prev) => {
          if (qpNormalized && options.some((o) => o.key === qpNormalized)) return qpNormalized;
          return prev && options.some((o) => o.key === prev) ? prev : nextDefault;
        });
      } catch (e) {
        if (cancelled) return;
        presentMutationError(e, "Failed to load report types");
      } finally {
        if (!cancelled) setReportTypesLoading(false);
      }
    };
    void loadReportTypes();
    return () => {
      cancelled = true;
    };
  }, [location.search]);
  const reloadReportTypes = useCallback(async () => {
    setReportTypesLoading(true);
    try {
      const data = await getReportTypes();
      const options = mapReportTypeOptionsForDisplay(
        Array.isArray(data.report_types) ? data.report_types : []
      );
      setReportTypeOptions(options);
      const hasDefault = options.some((o) => o.key === data.default_report_type);
      const fallback = options[0]?.key || "";
      const nextDefault = hasDefault ? data.default_report_type : fallback;
      setSelectedReportType((prev) => (prev && options.some((o) => o.key === prev) ? prev : nextDefault));
    } catch (e) {
      presentMutationError(e, "Failed to load report types");
    } finally {
      setReportTypesLoading(false);
    }
  }, []);

  const reloadStrategies = useCallback(async () => {
    setStrategiesLoading(true);
    try {
      const data = await listReportStrategies({
        include_archived: false,
        limit: 200,
        offset: 0,
        ...(effectiveBrokerAccountCode
          ? {
              broker_account_code: effectiveBrokerAccountCode,
              ...(activeBrokerName ? { broker_name: activeBrokerName } : {}),
            }
          : {}),
      });
      const options = Array.isArray(data.strategies) ? data.strategies : [];
      setStrategyOptions(options);
      const params = new URLSearchParams(
        location.search.startsWith("?") ? location.search.slice(1) : location.search
      );
      const qpStrategyId = params.get("strategy_id")?.trim();
      setSelectedStrategyId((prev) => {
        if (qpStrategyId && options.some((s) => s.id === qpStrategyId)) return qpStrategyId;
        if (prev && options.some((s) => s.id === prev)) return prev;
        // Keep canvas/workspace prefill even if the list has not included it yet.
        if (prefillStrategyId && prev === prefillStrategyId) return prev;
        if (prefillStrategyId && options.some((s) => s.id === prefillStrategyId)) {
          return prefillStrategyId;
        }
        return options[0]?.id || prev || "";
      });
      if (
        (qpStrategyId && options.some((s) => s.id === qpStrategyId)) ||
        prefillStrategyId
      ) {
        setStrategyMode("existing");
      }
    } catch (e) {
      presentMutationError(e, "Failed to load strategies");
    } finally {
      setStrategiesLoading(false);
    }
  }, [activeBrokerName, effectiveBrokerAccountCode, location.search, prefillStrategyId]);

  const openEditStrategyModal = useCallback(() => {
    const selected = strategyOptions.find((s) => s.id === selectedStrategyId);
    if (!selected) return;
    setEditStrategyId(selected.id);
    setEditStrategyName(selected.name || "");
    setEditStrategyDescription(selected.description || "");
    setEditStrategyArchived(Boolean(selected.is_archived));
    onEditStrategyOpen();
  }, [onEditStrategyOpen, selectedStrategyId, strategyOptions]);

  const onSaveEditedStrategy = useCallback(async () => {
    const name = editStrategyName.trim();
    const description = editStrategyDescription.trim();
    if (!editStrategyId || !name || !description) {
      toastError("Name and description required");
      return;
    }
    setSavingStrategy(true);
    try {
      await updateReportStrategy(editStrategyId, {
        name: name.slice(0, STRATEGY_NAME_SOFT_LIMIT),
        description: description.slice(0, STRATEGY_DESCRIPTION_SOFT_LIMIT),
        is_archived: editStrategyArchived,
      });
      toast.success("Strategy updated");
      onEditStrategyClose();
      await reloadStrategies();
    } catch (e) {
      presentMutationError(e, "Update failed");
    } finally {
      setSavingStrategy(false);
    }
  }, [
    editStrategyArchived,
    editStrategyDescription,
    editStrategyId,
    editStrategyName,
    onEditStrategyClose,
    reloadStrategies,
  ]);

  const verifyMarketDataAvailability = useCallback(
    async (tradeSymbol: string, assetClass?: string) => {
      const trimmed = tradeSymbol.trim();
      if (!trimmed) return null;
      setMarketDataAvailabilityVerifiedForSymbol(null);
      const result = await instrumentResolve.resolve(trimmed, assetClass);
      if (result?.asset_class) setResolvedAssetClass(result.asset_class);
      setMarketDataAvailabilityVerifiedForSymbol(trimmed);
      return result;
    },
    [instrumentResolve]
  );

  const onSymbolChange = useCallback(
    (value: string) => {
      setMarketDataConnectGate(null);
      setSymbol(value);
      setMarketDataAvailabilityVerifiedForSymbol(null);
      instrumentResolve.reset();
      setResolvedAssetClass(undefined);
      const row = availableInstruments.find((r) => r.symbol === value);
      if (!row) return;
      if (
        shouldGuideInstrumentMarketDataSetup(row) ||
        marketDataConnectGateFromInstrumentRow(row) ||
        marketDataOhlcBlockedNoticeFromInstrumentRow(row) ||
        instrumentChartsBlocked(row)
      ) {
        return;
      }
      if (needsMarketDataSourceChoiceForRow(row)) {
        return;
      }
      if (needsMarketDataAvailabilityResolveForRow(row)) {
        void verifyMarketDataAvailability(value, row.asset_class);
        return;
      }
      if (isInstrumentMarketDataReadyForCharts(row)) {
        instrumentResolve.seedFromAvailableInstrumentRow(row);
        if (row.asset_class) setResolvedAssetClass(row.asset_class);
      }
    },
    [
      availableInstruments,
      instrumentResolve,
      verifyMarketDataAvailability,
    ]
  );

  const onSymbolBlur = useCallback(
    async (symbolOverride?: string) => {
      const trimmed = (symbolOverride ?? symbol).trim();
      if (!trimmed) {
        instrumentResolve.reset();
        setResolvedAssetClass(undefined);
        setMarketDataAvailabilityVerifiedForSymbol(null);
        return;
      }
      const row = availableInstruments.find((r) => r.symbol === trimmed);
      if (row && needsMarketDataSourceChoiceForRow(row)) return;
      if (
        row &&
        (marketDataOhlcBlockedNoticeFromInstrumentRow(row) || instrumentChartsBlocked(row))
      ) {
        return;
      }
      if (row && needsMarketDataAvailabilityResolveForRow(row)) {
        if (marketDataAvailabilityVerifiedForSymbol === trimmed) return;
        await verifyMarketDataAvailability(trimmed, resolvedAssetClass ?? row.asset_class);
        return;
      }
      if (row && isInstrumentMarketDataReadyForCharts(row)) {
        instrumentResolve.seedFromAvailableInstrumentRow(row);
        if (row.asset_class) setResolvedAssetClass(row.asset_class);
        return;
      }
      if (!row) return;
      const result = await instrumentResolve.resolve(trimmed, resolvedAssetClass ?? row.asset_class);
      if (result?.asset_class) setResolvedAssetClass(result.asset_class);
    },
    [
      availableInstruments,
      marketDataAvailabilityVerifiedForSymbol,
      symbol,
      resolvedAssetClass,
      instrumentResolve,
      verifyMarketDataAvailability,
    ]
  );

  const selectedInstrumentRow = useMemo(
    () => availableInstruments.find((row) => row.symbol === symbol.trim()),
    [availableInstruments, symbol]
  );

  const proactiveMarketDataConnectGate = useMemo(() => {
    if (!showMarketContextFields || !symbol.trim()) return null;
    return (
      wizardMdGate.connectGate ??
      marketDataOhlcBlockedNoticeFromInstrumentRow(selectedInstrumentRow)
    );
  }, [
    selectedInstrumentRow,
    showMarketContextFields,
    symbol,
    wizardMdGate.connectGate,
  ]);

  const resolveMarketDataConnectGate = useMemo(
    () => marketDataConnectGateFromResolveResult(instrumentResolve.resolvedResult),
    [instrumentResolve.resolvedResult]
  );

  const connectorMarketDataConnectGate = useMemo(
    () =>
      marketDataConnectGateFromConnectorRequired(instrumentResolve.pendingConnectorRequired),
    [instrumentResolve.pendingConnectorRequired]
  );

  const activeMarketDataConnectGate = useMemo(
    () =>
      marketDataConnectGate ??
      connectorMarketDataConnectGate ??
      resolveMarketDataConnectGate ??
      proactiveMarketDataConnectGate,
    [
      connectorMarketDataConnectGate,
      marketDataConnectGate,
      proactiveMarketDataConnectGate,
      resolveMarketDataConnectGate,
    ]
  );
  const marketDataConnectGateBlocked = Boolean(activeMarketDataConnectGate);

  const selectedInstrumentMdReady = isInstrumentMarketDataReadyForCharts(selectedInstrumentRow);

  const openInstrumentMarketDataSetup = useCallback(
    (focusSymbol?: string) => {
      if (wizardMdGate.connectGate) {
        wizardMdGate.openMarketDataSetup(focusSymbol);
        return;
      }
      const code = effectiveBrokerAccountCode?.trim();
      if (!code) return;
      void openSetup(code, {
        mandatory: false,
        forceOpen: true,
        focusSymbol:
          focusSymbol?.trim() ||
          activeMarketDataConnectGate?.symbol?.trim() ||
          symbol.trim() ||
          undefined,
      });
    },
    [
      activeMarketDataConnectGate?.symbol,
      effectiveBrokerAccountCode,
      openSetup,
      symbol,
      wizardMdGate,
    ]
  );

  const savedOhlcMdBroker = useMemo(() => {
    const selected = selectedInstrumentRow?.selected_market_data_source;
    return (
      selected?.market_data_broker ??
      selected?.broker ??
      instrumentResolve.resolvedResult?.market_data_broker ??
      instrumentResolve.resolvedResult?.md_source ??
      null
    );
  }, [instrumentResolve.resolvedResult, selectedInstrumentRow]);

  const previewMarketDataUnavailable = useMemo(
    () => marketDataUnavailableFromCandleTimeframePreview(candleTimeframePreview),
    [candleTimeframePreview]
  );

  const activeMarketDataUnavailable = useMemo(() => {
    if (activeMarketDataConnectGate) return null;
    if (selectedInstrumentMdReady && !previewMarketDataUnavailable) {
      return null;
    }
    return marketDataUnavailableGate ?? previewMarketDataUnavailable;
  }, [
    activeMarketDataConnectGate,
    marketDataUnavailableGate,
    previewMarketDataUnavailable,
    selectedInstrumentMdReady,
  ]);
  const marketDataHistoryBlocked = Boolean(activeMarketDataUnavailable);

  const candleTimeframePreviewChecking = useMemo(() => {
    if (!showMarketContextFields || !symbol.trim() || !candleTimeframe || !effectiveBrokerAccountCode) {
      return false;
    }
    return candleTimeframePreviewLoading;
  }, [
    candleTimeframe,
    candleTimeframePreviewLoading,
    effectiveBrokerAccountCode,
    showMarketContextFields,
    symbol,
  ]);

  const marketDataAvailabilityChecking = useMemo(
    () => {
      if (wizardMdGate.scanning) return true;
      if (proactiveMarketDataConnectGate || activeMarketDataConnectGate) return false;
      return isMarketDataAvailabilityVerificationPending({
        row: selectedInstrumentRow,
        symbol,
        verifiedSymbol: marketDataAvailabilityVerifiedForSymbol,
        resolveLoading: instrumentResolve.state.kind === "loading",
      });
    },
    [
      activeMarketDataConnectGate,
      instrumentResolve.state.kind,
      marketDataAvailabilityVerifiedForSymbol,
      proactiveMarketDataConnectGate,
      selectedInstrumentRow,
      symbol,
      wizardMdGate.scanning,
    ]
  );

  useEffect(() => {
    setMarketDataConnectGate(null);
    setMarketDataUnavailableGate(null);
    setWizardResumeStepKey(null);
  }, [symbol, effectiveBrokerAccountCode]);

  useEffect(() => {
    setMarketDataUnavailableGate(null);
  }, [symbol, candleTimeframe, dateRange.startDate, dateRange.endDate, savedOhlcMdBroker]);

  useEffect(() => {
    if (instrumentSetupWasOpenRef.current && !instrumentSetupOpen) {
      setMarketDataAvailabilityVerifiedForSymbol(null);
      instrumentResolve.reset();
      void loadAvailableInstruments();
    }
    instrumentSetupWasOpenRef.current = instrumentSetupOpen;
  }, [instrumentSetupOpen, instrumentResolve, loadAvailableInstruments]);

  useEffect(() => {
    if (!selectedInstrumentRow || !selectedInstrumentMdReady) return;
    if (wizardMdGate.connectGate || instrumentChartsBlocked(selectedInstrumentRow)) return;
    setMarketDataConnectGate(null);
    setMarketDataUnavailableGate(null);
    instrumentResolve.seedFromAvailableInstrumentRow(selectedInstrumentRow);
    if (selectedInstrumentRow.asset_class) {
      setResolvedAssetClass(selectedInstrumentRow.asset_class);
    }
    setMarketDataAvailabilityVerifiedForSymbol(symbol.trim() || null);
  }, [
    instrumentResolve,
    selectedInstrumentMdReady,
    selectedInstrumentRow,
    symbol,
    wizardMdGate.connectGate,
  ]);

  useEffect(() => {
    if (!activeMarketDataConnectGate) {
      setWizardResumeStepKey(null);
    }
  }, [activeMarketDataConnectGate]);

  useEffect(() => {
    if (!proactiveMarketDataConnectGate) return;
    if (instrumentResolve.pendingConnectorRequired) {
      instrumentResolve.dismissConnectorRequired();
    }
  }, [instrumentResolve, proactiveMarketDataConnectGate]);

  useEffect(() => {
    if (!showMarketContextFields || !symbol.trim() || instrumentsLoading) return;
    const row = selectedInstrumentRow;
    if (!row || !needsMarketDataAvailabilityResolveForRow(row)) return;
    if (proactiveMarketDataConnectGate || instrumentChartsBlocked(row)) return;
    if (instrumentResolve.state.kind === "loading") return;
    if (marketDataAvailabilityVerifiedForSymbol === symbol.trim()) return;
    if (resolveMarketDataConnectGate || connectorMarketDataConnectGate) return;

    void verifyMarketDataAvailability(symbol.trim(), resolvedAssetClass ?? row.asset_class);
  }, [
    connectorMarketDataConnectGate,
    instrumentResolve.state.kind,
    instrumentsLoading,
    marketDataAvailabilityVerifiedForSymbol,
    proactiveMarketDataConnectGate,
    resolvedAssetClass,
    resolveMarketDataConnectGate,
    selectedInstrumentRow,
    showMarketContextFields,
    symbol,
    verifyMarketDataAvailability,
  ]);

  const instrumentNeedsOhlcSourceChoice = useMemo(
    () => Boolean(selectedInstrumentRow && needsMarketDataSourceChoiceForRow(selectedInstrumentRow)),
    [selectedInstrumentRow]
  );

  const instrumentMarketDataSetupRecommended = useMemo(
    () =>
      Boolean(wizardMdGate.connectGate) ||
      shouldGuideInstrumentMarketDataSetup(selectedInstrumentRow) ||
      Boolean(marketDataOhlcBlockedNoticeFromInstrumentRow(selectedInstrumentRow)),
    [selectedInstrumentRow, wizardMdGate.connectGate]
  );

  const mdBlocked = useMemo(
    () =>
      computeGenerateMdBlocked({
        selectedRow: selectedInstrumentRow,
        resolveMdBlocked: instrumentResolve.mdBlocked,
        resolveHasError: instrumentResolve.state.kind === "error",
        marketDataConnectGateBlocked,
      }),
    [
      instrumentResolve.mdBlocked,
      instrumentResolve.state.kind,
      marketDataConnectGateBlocked,
      selectedInstrumentRow,
    ]
  );

  useEffect(() => {
    if (!showMarketContextFields || !symbol.trim() || !candleTimeframe || !effectiveBrokerAccountCode) {
      setCandleTimeframePreview(null);
      setCandleTimeframePreviewLoading(false);
      setCandleTimeframePreviewFailed(false);
      return;
    }

    setCandleTimeframePreviewFailed(false);

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setCandleTimeframePreviewLoading(true);
      void (async () => {
        try {
          const preview = await fetchCandleTimeframePreview({
            symbol: symbol.trim(),
            broker_account_code: effectiveBrokerAccountCode,
            candle_timeframe: candleTimeframe,
            ...analyticsBundleWindow,
          });
          if (!cancelled) {
            setCandleTimeframePreview(preview);
            setCandleTimeframePreviewFailed(false);
            if (preview?.market_data_readiness?.can_generate !== false && !wizardMdGate.connectGate) {
              setMarketDataUnavailableGate(null);
              setMarketDataConnectGate(null);
            }
          }
        } catch (e) {
          if (!cancelled) {
            setCandleTimeframePreview(null);
            const previewError = candleTimeframePreviewFetchErrorState(e);
            if (previewError) {
              setCandleTimeframePreviewFailed(false);
              if (previewError.connectGate) {
                setMarketDataConnectGate(previewError.connectGate);
                setMarketDataUnavailableGate(null);
              } else {
                setMarketDataUnavailableGate(previewError.unavailablePayload);
              }
              setMarketDataAvailabilityVerifiedForSymbol(symbol.trim());
            } else {
              setCandleTimeframePreviewFailed(true);
            }
          }
        } finally {
          if (!cancelled) setCandleTimeframePreviewLoading(false);
        }
      })();
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    showMarketContextFields,
    symbol,
    candleTimeframe,
    effectiveBrokerAccountCode,
    analyticsBundleWindow,
    instrumentSetupOpen,
    wizardMdGate.connectGate,
  ]);

  const candleTimeframePreviewUi = useMemo(() => {
    if (!candleTimeframe || !showMarketContextFields || !symbol.trim()) return null;
    return buildCandleTimeframePreviewUi({
      preview: candleTimeframePreview,
      requestedTimeframe: candleTimeframe,
      windowFields: analyticsBundleWindow,
      windowSummaryLabel,
      loading: candleTimeframePreviewLoading,
      fetchFailed: candleTimeframePreviewFailed,
      includeExcursionNote: isTradeManagementReportType(selectedReportType),
    });
  }, [
    analyticsBundleWindow,
    candleTimeframe,
    candleTimeframePreview,
    candleTimeframePreviewFailed,
    candleTimeframePreviewLoading,
    selectedReportType,
    showMarketContextFields,
    symbol,
    windowSummaryLabel,
  ]);

  const [candleBudgetExceedsFromGate, setCandleBudgetExceedsFromGate] = useState(false);

  const candleBudgetExceeds = useMemo(() => {
    if (!showMarketContextFields) return false;
    if (candleTimeframePreviewUi?.exceedsBudget) return true;
    if (candleTimeframePreview?.exceeds_budget) return true;
    return candleBudgetExceedsFromGate;
  }, [
    candleBudgetExceedsFromGate,
    candleTimeframePreview?.exceeds_budget,
    candleTimeframePreviewUi?.exceedsBudget,
    showMarketContextFields,
  ]);

  /** Explicit window for SimulatorCandleBudgetGate — inject cohort span when All time. */
  const candleBudgetWindowFields = useMemo(() => {
    if (
      analyticsBundleWindow.local_start_date &&
      analyticsBundleWindow.local_end_date
    ) {
      return analyticsBundleWindow;
    }
    if (analyticsBundleWindow.start_date && analyticsBundleWindow.end_date) {
      return analyticsBundleWindow;
    }
    const cohort = candleTimeframePreview?.cohort;
    const start = cohort?.start_date?.trim();
    const end = cohort?.end_date?.trim();
    if (start && end) {
      return {
        ...analyticsBundleWindow,
        start_date: start,
        end_date: end,
      };
    }
    return analyticsBundleWindow;
  }, [analyticsBundleWindow, candleTimeframePreview?.cohort]);

  const parseBudgetPeriodYmd = useCallback((raw: string): Date | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
    if (!m) return null;
    const y = parseInt(m[1]!, 10);
    const mo = parseInt(m[2]!, 10);
    const d = parseInt(m[3]!, 10);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
    return new Date(y, mo - 1, d, 0, 0, 0, 0);
  }, []);

  const handleSelectCandleBudgetPeriod = useCallback(
    (startYmd: string, endYmd: string) => {
      const start = parseBudgetPeriodYmd(startYmd);
      const end = parseBudgetPeriodYmd(endYmd);
      if (!start || !end) return;
      setDateRange({ startDate: start, endDate: end });
      setSelectedPresetId(null);
      setCandleBudgetExceedsFromGate(false);
    },
    [parseBudgetPeriodYmd]
  );

  const onGenerate = useCallback(async () => {
    if (
      !effectiveBrokerAccountCode ||
      !reportTitle.trim() ||
      !selectedReportType ||
      (symbolRequired && !symbol.trim()) ||
      (showPerformanceReviewSymbolFields &&
        !performanceReviewAllSymbols &&
        performanceReviewSymbols.length === 0) ||
      mdBlocked ||
      marketDataConnectGateBlocked ||
      marketDataHistoryBlocked ||
      candleTimeframePreviewChecking ||
      candleBudgetExceeds ||
      marketDataAvailabilityChecking ||
      instrumentResolve.pendingAssetClassPayload ||
      instrumentResolve.pendingMarketDataSourceChoice ||
      instrumentResolve.pendingConsent ||
      instrumentNeedsOhlcSourceChoice ||
      !isTradeScopeDateRangeValid(dateRange) ||
      hasInflightReport ||
      weeklyReportQuotaReached
    ) {
      return;
    }
    const allowedReportTypes = new Set(reportTypeOptions.map((o) => o.key));
    if (!allowedReportTypes.has(selectedReportType)) {
      toastError("Invalid report type", {
        description: "Selected report type is no longer supported. Please refresh and try again.",
      });
      return;
    }
    const titleTrim = reportTitle.trim();
    const embedMode = Boolean(options?.embedMode);

    if (includeMarketContextForGenerate && symbol.trim()) {
      const scanGate = await wizardMdGate.checkBeforeRun();
      if (scanGate) {
        setMarketDataConnectGate(scanGate);
        setWizardResumeStepKey("report_details");
        toastError(scanGate.message, {
          description: scanGate.action,
        });
        if (embedMode) {
          options?.onEmbedGenerateFailed?.({ keepDialogOpen: true });
        }
        return;
      }

      const preflight = await instrumentResolve.resolve(
        symbol.trim(),
        resolvedAssetClass ?? selectedInstrumentRow?.asset_class
      );
      const preflightGate = marketDataConnectGateFromResolveResult(preflight);
      if (preflightGate) {
        setMarketDataConnectGate(preflightGate);
        setWizardResumeStepKey("report_details");
        toastError(preflightGate.message, {
          description: preflightGate.action,
        });
        if (embedMode) {
          options?.onEmbedGenerateFailed?.({ keepDialogOpen: true });
        }
        return;
      }
    }

    setGenerating(true);
    const placementOverride = embedMode
      ? options?.onEmbedGenerateStart?.({
          title: titleTrim.slice(0, REPORT_TITLE_MAX) || "New report",
          strategyEntityId:
            strategyMode === "existing" && selectedStrategyId.trim()
              ? selectedStrategyId.trim()
              : null,
        })
      : undefined;

    try {
      const strategyText = strategyDescription.trim();
      const strategyTitle = strategyName.trim();

      const strategyPayload =
        strategyMode === "none"
          ? {}
          : strategyMode === "existing"
            ? selectedStrategyId
              ? { strategy_id: selectedStrategyId }
              : {}
            : {
                strategy_name: strategyTitle ? strategyTitle.slice(0, STRATEGY_NAME_SOFT_LIMIT) : undefined,
                strategy_description: strategyText
                  ? strategyText.slice(0, STRATEGY_DESCRIPTION_SOFT_LIMIT)
                  : undefined,
              };

      const marketContextPayload = includeMarketContextForGenerate
        ? {
            symbol: symbol.trim(),
            asset_class: resolvedAssetClass,
            candle_timeframe: candleTimeframe,
            market_context_spec: marketContextSpec,
            market_context_version:
              marketContextSpec === "pine_v1" ? "market_context.pine_v1" : "market_context.backend_v1",
          }
        : {};

      const performanceReviewSymbolsPayload = showPerformanceReviewSymbolFields
        ? buildPerformanceReviewSymbolsPayload({
            allSymbols: performanceReviewAllSymbols,
            selectedSymbols: performanceReviewSymbols,
          })
        : {};

      const { report: created, accepted } = await generateReport({
        ...analyticsBundleWindow,
        broker_account_code: effectiveBrokerAccountCode || undefined,
        trade_side_filter: tradeSideFilter,
        report_type: selectedReportType,
        ...marketContextPayload,
        ...performanceReviewSymbolsPayload,
        market_context_spec: marketContextSpec,
        market_context_version:
          marketContextSpec === "pine_v1" ? "market_context.pine_v1" : "market_context.v1",
        candle_timeframe: candleTimeframe || DEFAULT_REPORT_CANDLE_TIMEFRAME,
        ...strategyPayload,
        title: titleTrim.slice(0, REPORT_TITLE_MAX),
        ...(options?.workspaceContext
          ? {
              workspaceId: options.workspaceContext.workspaceId,
              parentNodeId:
                placementOverride?.parentNodeId ?? options.workspaceContext.parentNodeId,
              position: placementOverride?.position ?? options.workspaceContext.position,
            }
          : {}),
      });

      toast.success("Report generation started", {
        description: embedMode
          ? "Your report is generating on this workspace."
          : "You can continue working while we generate your report.",
      });

      if (embedMode) {
        options?.onReportGenerated?.({ report: created, accepted });
        return;
      }

      const pendingOnList = {
        id: created.id,
        title: getReportDisplayTitle(created) || titleTrim.slice(0, REPORT_TITLE_MAX) || "New report",
        createdAt: created.created_at ?? new Date().toISOString(),
      };

      if (accepted || created.status === "processing") {
        toast.success("Report queued", {
          description: "Generating your report… Watch the list for progress.",
        });
        history.push({
          pathname: "/reports",
          state: { pendingGeneratedReport: pendingOnList },
        });
        return;
      }

      toast.success("Report created", {
        description: "Your report will appear in the list when ready.",
      });
      history.push({
        pathname: "/reports",
        state: { pendingGeneratedReport: pendingOnList },
      });
    } catch (e) {
      if (e instanceof MarketDataUnavailableError) {
        setMarketDataUnavailableGate(e.payload);
        setWizardResumeStepKey(
          showMarketContextFields ? "report_details" : "generate"
        );
        toastError("Report can't use this candle timeframe", {
          description: e.payload.message,
        });
        if (embedMode) {
          options?.onEmbedGenerateFailed?.({ keepDialogOpen: true });
        }
        return;
      }

      const connectGateFromError = marketDataConnectGateFromApiError(
        e instanceof MarketDataUnresolvedError ? e.body : e
      );
      if (connectGateFromError) {
        setMarketDataConnectGate(connectGateFromError);
        setWizardResumeStepKey(
          showMarketContextFields ? "report_details" : "generate"
        );
        toastError(connectGateFromError.message, {
          description: connectGateFromError.action,
        });
        if (embedMode) {
          options?.onEmbedGenerateFailed?.({ keepDialogOpen: true });
        } else {
          history.replace({
            pathname: "/reports",
            state: { clearPendingGeneratedReport: true },
          });
        }
        return;
      }

      if (handleMarketDataUnresolvedError(e, openSetup, effectiveBrokerAccountCode)) {
        if (embedMode) {
          options?.onEmbedGenerateFailed?.({ keepDialogOpen: true });
        } else {
          history.replace({
            pathname: "/reports",
            state: { clearPendingGeneratedReport: true },
          });
        }
        return;
      }

      if (embedMode) {
        options?.onEmbedGenerateFailed?.({ keepDialogOpen: false });
        toastError("Couldn't create report", {
          description: "Please try again.",
        });
        return;
      }

      const reportError = e as ReportsApiError;
      const budgetExceeded =
        reportError instanceof ReportsApiError &&
        (reportError.errorCode === "candle_bar_budget_exceeded" ||
          reportError.slug === "reports.generate.candle_bar_budget_exceeded" ||
          /candle.?bar.?budget|too many candles/i.test(
            reportError.detail || reportError.message || ""
          ));
      const invalidReportType =
        reportError instanceof ReportsApiError &&
        reportError.status === 422 &&
        /report_type/i.test(reportError.detail || reportError.message || "");
      const badWindow =
        reportError instanceof ReportsApiError &&
        reportError.status === 400 &&
        reportError.errorCode !== "symbol_required" &&
        !budgetExceeded &&
        /date|timeframe|local_|timezone|window/i.test(reportError.detail || reportError.message || "");

      if (budgetExceeded) {
        toastError("Candle bar budget exceeded", {
          description:
            reportError.message?.trim() ||
            "Shorten the date range or choose a larger candle timeframe, then try again.",
        });
      } else if (invalidReportType) {
        toastError("Failed to generate report", {
          description: "Selected report type is no longer supported. Please refresh and try again.",
        });
      } else if (badWindow) {
        toastError("Failed to generate report", {
          description: "Check your date range and timezone settings, then try again.",
        });
      } else {
        presentMutationError(e, "Failed to generate report");
      }
      history.replace({
        pathname: "/reports",
        state: {
          clearPendingGeneratedReport: true,
        },
      });
    } finally {
      setGenerating(false);
    }
  }, [
    analyticsBundleWindow,
    candleTimeframe,
    dateRange.endDate,
    dateRange.startDate,
    effectiveBrokerAccountCode,
    hasInflightReport,
    weeklyReportQuotaReached,
    history,
    includeMarketContextForGenerate,
    marketContextSpec,
    reportTitle,
    reportTypeOptions,
    selectedReportType,
    selectedStrategyId,
    strategyDescription,
    strategyMode,
    strategyName,
    tradeSideFilter,
    symbol,
    performanceReviewAllSymbols,
    performanceReviewSymbols,
    showPerformanceReviewSymbolFields,
    resolvedAssetClass,
    mdBlocked,
    marketDataConnectGateBlocked,
    candleBudgetExceeds,
    instrumentResolve.pendingAssetClassPayload,
    instrumentResolve.pendingMarketDataSourceChoice,
    instrumentResolve.pendingConsent,
    instrumentNeedsOhlcSourceChoice,
    openSetup,
    options?.embedMode,
    options?.onEmbedGenerateFailed,
    options?.onEmbedGenerateStart,
    options?.onReportGenerated,
    options?.workspaceContext,
    wizardMdGate.checkBeforeRun,
    selectedInstrumentRow?.asset_class,
  ]);

  const generateReadyInput = useMemo(
    () => ({
      rateLimitCoolingDown: reportGenerateRateLimit.isCoolingDown,
      rateLimitRetryAfterSeconds: reportGenerateRateLimit.retryAfterSeconds,
      inflightCheckLoading:
        options?.hasInflightReport === undefined && !fetchedInflightReport,
      hasInflightReport,
      weeklyReportQuotaReached,
      weeklyReportQuotaMessage,
      effectiveBrokerAccountCode,
      reportTitle,
      symbolRequired,
      symbol,
      performanceReviewAllSymbols,
      performanceReviewSymbols,
      showPerformanceReviewSymbolFields,
      mdBlocked,
      marketDataConnectGateBlocked,
      marketDataHistoryBlocked,
      marketDataHistoryBlockedMessage: activeMarketDataUnavailable?.message,
      candleTimeframePreviewChecking,
      candleBudgetExceeds,
      marketDataAvailabilityChecking,
      pendingAssetClassPayload: instrumentResolve.pendingAssetClassPayload,
      pendingMarketDataSourceChoice: instrumentResolve.pendingMarketDataSourceChoice,
      pendingConsent: instrumentResolve.pendingConsent,
      instrumentNeedsOhlcSourceChoice,
      selectedReportType,
      candleTimeframe,
      reportTypeOptions,
      dateRange,
    }),
    [
      candleTimeframe,
      dateRange,
      effectiveBrokerAccountCode,
      fetchedInflightReport,
      hasInflightReport,
      weeklyReportQuotaReached,
      weeklyReportQuotaMessage,
      mdBlocked,
      marketDataConnectGateBlocked,
      marketDataHistoryBlocked,
      activeMarketDataUnavailable,
      candleTimeframePreviewChecking,
      candleBudgetExceeds,
      marketDataAvailabilityChecking,
      instrumentResolve.pendingAssetClassPayload,
      instrumentResolve.pendingConsent,
      instrumentResolve.pendingMarketDataSourceChoice,
      instrumentNeedsOhlcSourceChoice,
      options?.hasInflightReport,
      reportGenerateRateLimit.isCoolingDown,
      reportGenerateRateLimit.retryAfterSeconds,
      reportTitle,
      reportTypeOptions,
      selectedReportType,
      symbol,
      symbolRequired,
      performanceReviewAllSymbols,
      performanceReviewSymbols,
      showPerformanceReviewSymbolFields,
    ]
  );

  const canGenerate = useMemo(
    () => isGenerateReportReady(generateReadyInput),
    [generateReadyInput]
  );

  const generateBlockedReason = useMemo(
    () => getGenerateReportBlockedReason(generateReadyInput),
    [generateReadyInput]
  );

  const selectedReportTypeDescription = useMemo(() => {
    const fromApi = reportTypeOptions.find((o) => o.key === selectedReportType)?.description?.trim();
    if (fromApi) return fromApi;
    if (isTradeManagementReportType(selectedReportType)) return EXIT_PLACEMENT_SUBTITLE;
    return "";
  }, [reportTypeOptions, selectedReportType]);

  const generatingReportTitle = useMemo(() => {
    const trimmedTitle = reportTitle.trim();
    return trimmedTitle || "Report";
  }, [reportTitle]);

  return {
    generating,
    generatingReportTitle,
    onGenerate,
    canGenerate,
    wizardProps: {
      effectiveBrokerAccountCode,
      brokerAccountOptions,
      brokerAccountsLoading,
      onBrokerAccountChange,
      tradeSideFilter,
      onTradeSideFilterChange: setTradeSideFilter,
      showMarketContextFields,
      showPerformanceReviewSymbolFields,
      symbolRequired,
      symbol,
      performanceReviewAllSymbols,
      performanceReviewSymbols,
      onPerformanceReviewAllSymbolsChange: setPerformanceReviewAllSymbols,
      onPerformanceReviewSymbolsChange: setPerformanceReviewSymbols,
      onSymbolChange,
      onSymbolBlur: () => void onSymbolBlur(),
      instrumentResolveMessage:
        isInstrumentMarketDataReadyForCharts(selectedInstrumentRow)
          ? null
          : instrumentResolve.state.kind === "error"
            ? instrumentResolve.state.message
            : instrumentResolve.state.kind === "loading"
              ? "Checking symbol…"
              : null,
      mdBlocked,
      marketDataConnectGate: activeMarketDataConnectGate,
      marketDataUnavailable: activeMarketDataUnavailable,
      onMarketDataUnavailablePickCandleType: (timeframe: string) => {
        setCandleTimeframe(timeframe as typeof candleTimeframe);
      },
      onMarketDataUnavailableChangeSource: () => openInstrumentMarketDataSetup(),
      onMarketDataUnavailableConnectBroker: (broker: string) => {
        history.push(buildMarketDataConnectOptionHref(broker));
      },
      onMarketDataConnectNavigate: (href: string) => {
        if (href === MARKET_DATA_PREFERENCE_SETTINGS_PATH) {
          openInstrumentMarketDataSetup();
          return;
        }
        history.push(href);
      },
      marketDataAvailabilityChecking,
      resumeStepKey: wizardResumeStepKey,
      activeStepIndex: wizardActiveStepIndex,
      onActiveStepIndexChange: setWizardActiveStepIndex,
      marketDataSourceChoicePending: instrumentNeedsOhlcSourceChoice,
      instrumentNeedsOhlcSourceChoice,
      instrumentMarketDataSetupRecommended,
      onConfigureInstrumentMarketData: () => openInstrumentMarketDataSetup(),
      selectedInstrumentRow,
      candleTimeframePreviewUi,
      candleTimeframePreviewLoading,
      showCandleBudgetGate: showMarketContextFields && Boolean(candleTimeframe && symbol.trim()),
      candleBudgetExceeds,
      candleBudgetWindowFields,
      onSelectCandleBudgetPeriod: handleSelectCandleBudgetPeriod,
      onCandleBudgetExceedsChange: setCandleBudgetExceedsFromGate,
      availableInstruments,
      instrumentsSelectionHint,
      instrumentsLoading,
      onReloadAvailableInstruments: loadAvailableInstruments,
      dateRange,
      selectedPresetId,
      onDateRangeChange: handleDateRangeChange,
      reportTitle,
      onReportTitleChange: setReportTitle,
      reportTitleMax: REPORT_TITLE_MAX,
      selectedReportType,
      onReportTypeChange,
      reportTypeOptions,
      reportTypesLoading,
      onReloadReportTypes: reloadReportTypes,
      onReloadStrategies: reloadStrategies,
      selectedReportTypeDescription,
      candleTimeframe,
      onCandleTimeframeChange: setCandleTimeframe,
      strategyMode,
      onStrategyModeChange: setStrategyMode,
      strategyOptions: strategyOptionsForWizard,
      selectedStrategyId,
      onSelectedStrategyIdChange: setSelectedStrategyId,
      strategiesLoading,
      prefillStrategyName: effectivePrefillStrategyName || null,
      strategyName,
      onStrategyNameChange: setStrategyName,
      strategyNameLimit: STRATEGY_NAME_SOFT_LIMIT,
      strategyDescription,
      onStrategyDescriptionChange: setStrategyDescription,
      strategyDescriptionLimit: STRATEGY_DESCRIPTION_SOFT_LIMIT,
      onEditStrategy: openEditStrategyModal,
      canGenerate,
      generateBlockedReason,
      generating,
      onGenerate,
      hasInflightReport,
      weeklyReportQuotaReached,
      weeklyReportQuotaMessage,
      inflightCheckLoading: options?.hasInflightReport === undefined && !fetchedInflightReport,
      rateLimitCoolingDown: reportGenerateRateLimit.isCoolingDown,
      rateLimitRetryAfterSeconds: reportGenerateRateLimit.retryAfterSeconds,
      windowSummaryLabel,
    },
    instrumentResolveModals: {
      assetClassPayload: instrumentResolve.pendingAssetClassPayload,
      assetClassOpen: Boolean(instrumentResolve.pendingAssetClassPayload),
      onAssetClassClose: () => instrumentResolve.reset(),
      onAssetClassConfirm: (selection: {
        assetClass: string;
        marketDataBroker?: string;
        marketDataSource?: MarketDataSourceOption;
      }) => {
        void instrumentResolve.confirmAssetClassSelection(selection).then(async (r) => {
          if (r?.asset_class) setResolvedAssetClass(r.asset_class);
          await loadAvailableInstruments();
        });
      },
      onConnectBroker: (brokerSlug: string) => {
        history.push(buildConnectBrokerManagementOpenUrl({ brokerName: brokerSlug }));
      },
      quoteVariantOpen: Boolean(instrumentResolve.pendingQuoteVariantChoice),
      quoteVariantTradeSymbol:
        instrumentResolve.pendingQuoteVariantChoice?.tradeSymbol ?? symbol.trim(),
      quoteVariantCandidates: instrumentResolve.pendingQuoteVariantChoice?.candidates ?? [],
      quoteVariantSuggested: instrumentResolve.pendingQuoteVariantChoice?.suggested ?? null,
      onQuoteVariantClose: () => instrumentResolve.reset(),
      onQuoteVariantConfirm: (qualifiedSymbol: string) => {
        void instrumentResolve.confirmQuoteVariantSelection(qualifiedSymbol).then(async (r) => {
          if (r?.asset_class) setResolvedAssetClass(r.asset_class);
          await loadAvailableInstruments();
        });
      },
      savingQuoteVariant: instrumentResolve.savingQuoteVariant,
      connectorOpen:
        Boolean(instrumentResolve.pendingConnectorRequired) &&
        !activeMarketDataConnectGate,
      connectorPayload: instrumentResolve.pendingConnectorRequired,
      onConnectorClose: () => instrumentResolve.dismissConnectorRequired(),
      onConnectorConnectBroker: (brokerSlug: string) => {
        history.push(buildMarketDataConnectOptionHref(brokerSlug));
      },
      onConnectorOpenMarketDataPreferences: () => openInstrumentMarketDataSetup(),
      onConnectorUseCentralSource: () => instrumentResolve.openCentralSourceFromConnectorGate(),
      consentOpen: Boolean(instrumentResolve.pendingConsent),
      consentSymbol:
        instrumentResolve.pendingConsent?.symbol ?? symbol.trim(),
      consentAttribution: instrumentResolve.pendingConsent?.attribution ?? null,
      consentSource: instrumentResolve.pendingConsent?.source ?? null,
      onConsentClose: () => instrumentResolve.reset(),
      onGrantConsent: () => void instrumentResolve.grantConsent(),
    },
    editStrategyModal: {
      isOpen: editStrategyOpen,
      onClose: onEditStrategyClose,
      name: editStrategyName,
      onNameChange: setEditStrategyName,
      description: editStrategyDescription,
      onDescriptionChange: setEditStrategyDescription,
      isArchived: editStrategyArchived,
      onArchivedChange: setEditStrategyArchived,
      onSave: onSaveEditedStrategy,
      saving: savingStrategy,
      nameLimit: STRATEGY_NAME_SOFT_LIMIT,
      descriptionLimit: STRATEGY_DESCRIPTION_SOFT_LIMIT,
    },
  };
}
